import React, { useState, useEffect } from 'react';
import { 
  Upload, Image as ImageIcon, Save, CheckCircle, 
  AlertCircle, ChevronDown, ArrowRight, FolderPlus, Layers 
} from 'lucide-react';

interface SimpleCategory {
  id: string;
  name: string;
  level: number;
}

const AddCategoryPage: React.FC = () => {
  const [categoryName, setCategoryName] = useState('');
  const [parentCategory, setParentCategory] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [existingCategories, setExistingCategories] = useState<SimpleCategory[]>([]);

  const fetchExistingCategories = async () => {
    try {
      const response = await fetch('/nodejs/api/categories');
      if (!response.ok) throw new Error('Failed');
      const data: any[] = await response.json();
      const flatCategories: SimpleCategory[] = [];
      const flatten = (category: any, level: number = 0) => {
        flatCategories.push({ id: category.id.toString(), name: category.name, level });
        if (category.subCategories) {
          category.subCategories.forEach((sub: any) => flatten(sub, level + 1));
        }
      };
      data.forEach((cat) => flatten(cat, 0));
      setExistingCategories(flatCategories);
    } catch (error) { console.error(error); }
  };

  useEffect(() => { fetchExistingCategories(); }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('name', categoryName);
      if (parentCategory) formData.append('parentId', parentCategory);
      if (selectedImage) formData.append('categoryImage', selectedImage);

      const response = await fetch('/nodejs/api/categories', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to add category.');

      setMessage({ type: 'success', text: `Category "${categoryName}" created successfully!` });
      setCategoryName('');
      setParentCategory('');
      setSelectedImage(null);
      setPreviewUrl(null);
      fetchExistingCategories();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Something went wrong' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getParentName = () => {
    const parent = existingCategories.find(c => c.id === parentCategory);
    return parent ? parent.name : 'Top Level';
  };

  return (
    <div className="min-h-screen bg-white font-sans animate-fade-in-up">
      <div className="max-w-5xl mx-auto px-6 py-8">
        
        {/* Header with Icon */}
        <div className="mb-8 border-b border-gray-100 pb-6 flex items-center gap-5 relative overflow-hidden">
           {/* Icon Box */}
           <div className="w-14 h-14 bg-[#006d5b]/10 rounded-2xl flex items-center justify-center text-[#006d5b] shadow-sm relative z-10 shrink-0">
              <FolderPlus size={28} strokeWidth={1.5} />
           </div>
           
           {/* Text */}
           <div className="relative z-10">
             <h1 className="text-2xl font-black text-gray-900 tracking-tight">
               New Category
             </h1>
             <p className="text-gray-500 font-medium text-xs mt-1">Add and organize items in your master catalog.</p>
           </div>

           {/* Subtle Background Decoration */}
           <Layers size={120} className="absolute -right-10 -top-10 text-gray-50 pointer-events-none -z-0 rotate-12 opacity-50" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 relative z-10">
          
          {/* LEFT: FORM (Compact) */}
          <div className="md:col-span-7 space-y-6">
            
            <form onSubmit={handleSubmit} className="space-y-5">
              
              {message && (
                <div className={`p-3 rounded-lg flex items-center gap-2 text-xs font-bold ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                   {message.type === 'success' ? <CheckCircle size={16}/> : <AlertCircle size={16}/>}
                   {message.text}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="text-[11px] font-black text-gray-900 uppercase tracking-wider mb-2 block">Category Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  required
                  placeholder="Enter name..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-bold text-gray-900 focus:bg-white focus:border-[#006d5b] outline-none transition-all placeholder:text-gray-400"
                />
              </div>

              {/* Parent */}
              <div>
                <label className="text-[11px] font-black text-gray-900 uppercase tracking-wider mb-2 block">Hierarchy Level</label>
                <div className="relative">
                  <select
                    value={parentCategory}
                    onChange={(e) => setParentCategory(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-bold text-gray-700 appearance-none focus:bg-white focus:border-[#006d5b] outline-none cursor-pointer"
                  >
                    <option value="">• Main Root Category</option>
                    {existingCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {'\u00A0'.repeat(cat.level * 4)} {cat.level > 0 ? '└ ' : ''} {cat.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                </div>
              </div>

              {/* Image Upload - Compact */}
              <div>
                <label className="text-[11px] font-black text-gray-900 uppercase tracking-wider mb-2 block">Cover Image</label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 hover:border-[#006d5b] hover:bg-[#006d5b]/5 transition-all text-center group cursor-pointer relative bg-gray-50/30">
                   <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                   />
                   <div className="flex items-center justify-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-gray-400 group-hover:text-[#006d5b] transition-colors shadow-sm border border-gray-100">
                         {previewUrl ? <CheckCircle size={20} /> : <Upload size={20} />}
                      </div>
                      <div className="text-left">
                         <p className="text-xs font-bold text-gray-800 group-hover:text-[#006d5b] transition-colors">
                            {selectedImage ? selectedImage.name : "Upload Image"}
                         </p>
                         <p className="text-[10px] text-gray-400 font-bold">Max 2MB</p>
                      </div>
                   </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-2">
                 <button
                    type="submit"
                    disabled={isSubmitting || !categoryName}
                    className="w-full bg-[#006d5b] hover:bg-[#005a4b] text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest shadow-md shadow-[#006d5b]/20 flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-70 disabled:hover:scale-100"
                 >
                    {isSubmitting ? 'Processing...' : <><Save size={16} /> Save Category</>}
                 </button>
              </div>

            </form>
          </div>

          {/* RIGHT: PREVIEW (Compact & Sticky) */}
          <div className="md:col-span-5 hidden md:block">
             <div className="sticky top-6 border border-gray-100 rounded-2xl p-6 shadow-xl shadow-gray-100/50 bg-white">
                <div className="flex justify-between items-center mb-6">
                   <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Live Preview</h3>
                   <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded text-[9px] font-black uppercase">Active</span>
                </div>

                {/* The Card Preview */}
                <div className="relative aspect-video bg-gray-50 rounded-xl overflow-hidden flex items-center justify-center mb-4 border border-gray-100 group">
                   {previewUrl ? (
                      <img src={previewUrl} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt="Preview" />
                   ) : (
                      <div className="flex flex-col items-center text-gray-300">
                         <ImageIcon size={32} />
                         <span className="text-[10px] font-bold uppercase mt-2 tracking-widest">No Image</span>
                      </div>
                   )}
                   
                   {/* Gradient Overlay */}
                   <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent"></div>
                   
                   <div className="absolute bottom-3 left-4 right-4 text-white">
                      <p className="text-[9px] font-bold uppercase tracking-wider opacity-80">
                         {parentCategory ? 'Sub-Category' : 'Main'}
                      </p>
                      <h2 className="text-lg font-black leading-tight truncate">
                         {categoryName || "Category Name"}
                      </h2>
                   </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-50">
                   <span className="text-[10px] font-bold text-gray-400">Parent Node</span>
                   <span className="text-xs font-black text-gray-800 flex items-center gap-1">
                      {getParentName()} <ArrowRight size={10} className="text-[#006d5b]"/>
                   </span>
                </div>

             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AddCategoryPage;